import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class ConfirmDialogHotkeysService {
  private document = inject(DOCUMENT);
  private initialized = false;
  private mutationObserver: MutationObserver | null = null;

  initialize(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    this.document.addEventListener('keydown', this.handleKeydown, true);
    this.mutationObserver = new MutationObserver(() => {
      this.focusAcceptButton();
    });
    this.mutationObserver.observe(this.document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  private handleKeydown = (event: KeyboardEvent): void => {
    if (event.key !== 'Enter' || event.defaultPrevented) {
      return;
    }

    const target = event.target;
    if (target instanceof HTMLElement) {
      const tagName = target.tagName;
      if (tagName === 'TEXTAREA') {
        return;
      }
    }

    const visibleDialog = this.getVisibleDialog();
    if (!visibleDialog) {
      return;
    }

    const activeElement = this.document.activeElement;
    if (activeElement instanceof HTMLElement) {
      const tagName = activeElement.tagName;
      const isEditable = activeElement.isContentEditable
        || tagName === 'INPUT'
        || tagName === 'TEXTAREA'
        || tagName === 'SELECT';
      if (isEditable) {
        return;
      }
    }

    const acceptButton = this.getAcceptButton(visibleDialog);
    if (!acceptButton || acceptButton.disabled) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    acceptButton.click();
  };

  private focusAcceptButton(): void {
    const visibleDialog = this.getVisibleDialog();
    if (!visibleDialog) {
      return;
    }

    const activeElement = this.document.activeElement;
    if (
      activeElement instanceof HTMLElement
      && visibleDialog.contains(activeElement)
      && activeElement !== this.document.body
    ) {
      return;
    }

    const acceptButton = this.getAcceptButton(visibleDialog);
    if (!acceptButton || acceptButton.disabled) {
      return;
    }

    queueMicrotask(() => {
      const currentDialog = this.getVisibleDialog();
      const currentAcceptButton = currentDialog
        ? this.getAcceptButton(currentDialog)
        : null;

      if (currentAcceptButton === acceptButton && this.document.activeElement === this.document.body) {
        acceptButton.focus();
      }
    });
  }

  private getVisibleDialog(): HTMLElement | null {
    const dialog = this.document.querySelector('.p-dialog-mask.p-overlay-mask .p-confirmdialog');
    return dialog instanceof HTMLElement ? dialog : null;
  }

  private getAcceptButton(dialog: HTMLElement): HTMLButtonElement | null {
    const acceptButton = dialog.querySelector('[data-pc-section="acceptbutton"]')
      ?? dialog.querySelector('.p-confirmdialog-accept-button')
      ?? dialog.querySelector('button.p-button:not(.p-confirmdialog-reject-button)');

    return acceptButton instanceof HTMLButtonElement ? acceptButton : null;
  }
}
